import Axios from 'axios';
import { SelectedImages } from 'components/PhotoReports/PhotoReportDownloadDialog';
import download from 'downloadjs';
import { ReportDocumentType } from '../api/jobReportsApi';

export enum ReportType {
  INVENTORY_DETAIL_REPORT = 'inventory-detail-report',
  INVENTORY_PHOTO_REPORT = 'inventory-photo-report',
  INITIAL_ATTENDANCE_REPORT = 'initial-attendance-report',
  PHOTO_REPORT = 'photo-report',
  DYNAMIC_REPORT = 'dynamic-report',
  SITE_VISIT_REPORT = 'site-visit-report',
  COMPLETION_REPORT = 'completion-report',
  EQUIPMENT_STATUSES_REPORT = 'equipment-statuses-report',
}

export async function downloadAttendanceReport(
  jobId: string,
  letterheadId?: string,
  showJobDetailsHeader?: boolean,
  reportDocumentType?: ReportDocumentType,
  showPoweredByLogo?: boolean
): Promise<void> {
  const searchParams = new URLSearchParams(
    `letterheadId=${letterheadId}&showJobDetailsHeader=${showJobDetailsHeader}&reportDocumentType=${reportDocumentType}&showPoweredByLogo=${showPoweredByLogo}`
  ).toString();

  // Starts the report and returns its row; the file is downloaded from the reports list.
  const res = await Axios.get(
    `/api/v1/reports/${jobId}/${
      ReportType.INITIAL_ATTENDANCE_REPORT
    }${`?${searchParams}`}`
  );
  return res.data;
}

export async function downloadInventoryDetailReport({
  jobId,
  roomIds,
  dispositions,
  showJobDetailsHeader,
  includeClientLogo,
}: {
  jobId: string;
  roomIds?: string[];
  dispositions?: string[];
  showJobDetailsHeader?: boolean;
  includeClientLogo?: boolean;
}): Promise<void> {
  const searchParams = new URLSearchParams(
    `showJobDetailsHeader=${showJobDetailsHeader}&includeClientLogo=${includeClientLogo}`
  ).toString();
  // Starts the report and returns its row; the file is downloaded from the reports list.
  const res = await Axios.post(
    `/api/v1/reports/${jobId}/${
      ReportType.INVENTORY_DETAIL_REPORT
    }${`?${searchParams}`}`,
    { roomIds, dispositions }
  );
  return res.data;
}

export async function downloadInventoryPhotoReport({
  jobId,
  roomIds,
  dispositions,
  showJobDetailsHeader,
  letterheadId,
  inspectionIds,
  showPoweredByLogo,
}: {
  jobId: string;
  roomIds?: string[];
  dispositions?: string[];
  showJobDetailsHeader?: boolean;
  letterheadId?: string;
  inspectionIds?: string[];
  showPoweredByLogo?: boolean;
}): Promise<void> {
  const searchParams = new URLSearchParams(
    `showJobDetailsHeader=${showJobDetailsHeader}&letterheadId=${letterheadId}&showPoweredByLogo=${showPoweredByLogo}`
  ).toString();
  // Starts the report and returns its row; the file is downloaded from the reports list.
  const res = await Axios.post(
    `/api/v1/reports/${jobId}/${
      ReportType.INVENTORY_PHOTO_REPORT
    }${`?${searchParams}`}`,
    { roomIds, dispositions, inspectionIds }
  );
  return res.data;
}

export async function downloadPhotoReport({
  jobId,
  feedItemIds,
  letterheadId,
  showPhotoNoteHeader,
  showJobDetailsHeader,
  selectedImages,
  showPoweredByLogo,
}: {
  jobId: string;
  feedItemIds: string[];
  fileName?: string;
  letterheadId: string;
  showPhotoNoteHeader?: boolean;
  showJobDetailsHeader?: boolean;
  selectedImages?: SelectedImages[];
  showPoweredByLogo?: boolean;
}) {
  const searchParams = new URLSearchParams(
    `letterheadId=${letterheadId}&showPhotoNoteHeader=${showPhotoNoteHeader}&showJobDetailsHeader=${showJobDetailsHeader}&showPoweredByLogo=${showPoweredByLogo}`
  ).toString();

  // Starts the report and returns its row; the file is downloaded from the reports list.
  const res = await Axios.post(
    `/api/v1/reports/${jobId}/${ReportType.PHOTO_REPORT}${`?${searchParams}`}`,
    { feedItemIds, selectedImages }
  );
  return res.data;
}

export async function downloadDynamicReport({
  jobId,
  formId,
  fileName,
  letterheadId,
  showJobDetailsHeader,
  showPoweredByLogo,
}: {
  jobId: string;
  formId: string;
  fileName: string;
  letterheadId?: string;
  showJobDetailsHeader?: boolean;
  showPoweredByLogo?: boolean;
}) {
  const searchParams = new URLSearchParams(
    `letterheadId=${letterheadId}&showJobDetailsHeader=${showJobDetailsHeader}&showPoweredByLogo=${showPoweredByLogo}`
  ).toString();

  const res = await Axios.get(
    `/api/v1/reports/${jobId}/${
      ReportType.DYNAMIC_REPORT
    }/${formId}${`?${searchParams}`}`,
    {
      responseType: 'arraybuffer',
    }
  );

  const mime = res.headers['Content-Type'];
  download(res.data, fileName, mime);
}

export async function downloadSiteVisitReport({
  jobId,
  inspectionId,
  letterheadId,
  showJobDetailsHeader,
  reportDocumentType,
  showPoweredByLogo,
}: {
  jobId: string;
  inspectionId: string;
  letterheadId: string;
  showJobDetailsHeader?: boolean;
  reportDocumentType?: ReportDocumentType;
  showPoweredByLogo?: boolean;
}) {
  const searchParams = new URLSearchParams(
    `letterheadId=${letterheadId}&showJobDetailsHeader=${showJobDetailsHeader}&reportDocumentType=${reportDocumentType}&showPoweredByLogo=${showPoweredByLogo}`
  ).toString();

  // Starts the report and returns its row; the file is downloaded from the reports list.
  const res = await Axios.get(
    `/api/v1/reports/${jobId}/${inspectionId}/${
      ReportType.SITE_VISIT_REPORT
    }${`?${searchParams}`}`
  );

  return res.data;
}

export async function downloadCompletionReport({
  jobId,
  letterheadId,
  showJobDetailsHeader,
  selectedSiteVisitIds,
  showCompletionSummary,
  reportDocumentType,
  showPoweredByLogo,
}: {
  jobId: string;
  letterheadId: string;
  showJobDetailsHeader?: boolean;
  selectedSiteVisitIds?: string[];
  showCompletionSummary?: boolean;
  reportDocumentType?: ReportDocumentType;
  showPoweredByLogo?: boolean;
}) {
  // Starts the report and returns its row; the file is downloaded from the reports list.
  const res = await Axios.post(
    `/api/v1/reports/${jobId}/${ReportType.COMPLETION_REPORT}`,
    {
      letterheadId,
      showJobDetailsHeader,
      selectedSiteVisitIds,
      showCompletionSummary,
      reportDocumentType,
      showPoweredByLogo,
    }
  );

  return res.data;
}

export async function downloadEquipmentStatusesReport({
  fileName,
}: {
  fileName: string;
}) {
  const res = await Axios.get(
    `/api/v1/reports/${ReportType.EQUIPMENT_STATUSES_REPORT}`,
    {
      responseType: 'arraybuffer',
    }
  );

  const mime = res.headers['Content-Type'];
  download(res.data, fileName, mime);
}

export async function deleteJobForm({
  formId,
  jobId,
}: {
  formId: string;
  jobId: string;
}) {
  const res = await Axios.delete(`/api/v1/form/jobforms/${jobId}/${formId}`);
  return res.data;
}
